import { FC, useEffect, useState, useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { BoxV2 as Box } from 'portal-commons';

import { RcsCampaign } from '../../types';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isOptionRequired,
  isValidInput,
} from '../../validators';
import EmbeddedLinkPopup from '../EmbeddedLinkPopup';
import MessageFlowAttributePopup from '../MessageFlowAttributePopup';
import CampaignAttributeNew from '../CampaignAttributeNew';

type DefaultValues = Pick<
  RcsCampaign,
  | 'subscriberOptin'
  | 'optinMessage'
  | 'subscriberOptout'
  | 'optoutMessage'
  | 'subscriberHelp'
  | 'helpMessage'
  | 'embeddedLink'
  | 'embeddedLinkSample'
  | 'embeddedPhone'
  | 'ageGated'
  | 'directLending'
> & {
  optinKeywords: string[];
  optoutKeywords: string[];
  helpKeywords: string[];
};

interface ComplianceAttributesFormProps {
  campaign: RcsCampaign;
  onUpdate?: (updatedValues: Partial<RcsCampaign>) => void;
  editable?: boolean;
}

enum AdditionalSetupType {
  None,
  SubscriberOptin,
  SubscriberOptout,
  SubscriberHelp,
  EmbeddedLink,
}

const ComplianceAttributesForm: FC<ComplianceAttributesFormProps> = ({
  campaign,
  onUpdate,
  editable,
}) => {
  const expired = campaign.status === 'EXPIRED';
  const readonly = !editable || expired;

  const defaultValues = useMemo(() => {
    return {
      subscriberOptin: !!campaign.subscriberOptin,
      optinMessage: campaign.optinMessage || '',
      optinKeywords:
        campaign.optinKeywords?.length > 0
          ? campaign.optinKeywords.split(',')
          : [],
      subscriberOptout: !!campaign.subscriberOptout,
      optoutMessage: campaign.optoutMessage || '',
      optoutKeywords:
        campaign.optoutKeywords?.length > 0
          ? campaign.optoutKeywords.split(',')
          : [],
      subscriberHelp: !!campaign.subscriberHelp,
      helpMessage: campaign.helpMessage || '',
      helpKeywords:
        campaign.helpKeywords?.length > 0
          ? campaign.helpKeywords?.split(',')
          : [],
      embeddedLink: !!campaign.embeddedLink,
      embeddedLinkSample: campaign.embeddedLinkSample || '',
      embeddedPhone: !!campaign.embeddedPhone,
      ageGated: !!campaign.ageGated,
      directLending: !!campaign.directLending,
    };
  }, [campaign]);

  const { control, clearErrors, reset } = useForm<DefaultValues>({
    defaultValues,
    mode: 'onChange',
  });
  const {
    field: { value: subscriberOptin, onChange: setSubscriberOptin },
  } = useController({
    control,
    name: 'subscriberOptin',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: optinKeywords, onChange: setOptinKeywords },
  } = useController({
    control,
    name: 'optinKeywords',
  });
  const {
    field: {
      value: optinMessage,
      onChange: setOptinMessage,
      onBlur: validateOptinMessage,
    },
    fieldState: { error: optinMessageError },
  } = useController({
    control,
    name: 'optinMessage',
    rules: {
      validate: {
        ...(subscriberOptin
          ? {
              isValidInput,
              withValidMinLength: createMinLengthValidator(20, true),
              withValidMaxLength: createMaxLengthValidator(1024, true),
            }
          : {}),
      },
    },
  });
  const {
    field: { value: embeddedLink, onChange: setEmbeddedLink },
  } = useController({
    control,
    name: 'embeddedLink',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: {
      value: embeddedLinkSample,
      onChange: setEmbeddedLinkSample,
      onBlur: validateEmbeddedLinkSample,
    },
    fieldState: { error: embeddedLinkSampleError },
  } = useController({
    control,
    name: 'embeddedLinkSample',
    disabled: !embeddedLink,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });
  const {
    field: { value: subscriberOptout, onChange: setSubscriberOptout },
  } = useController({
    control,
    name: 'subscriberOptout',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: optoutKeywords, onChange: setOptoutKeywords },
  } = useController({
    control,
    name: 'optoutKeywords',
  });
  const {
    field: {
      value: optoutMessage,
      onChange: setOptoutMessage,
      onBlur: validateOptoutMessage,
    },
    fieldState: { error: optoutMessageError },
  } = useController({
    control,
    name: 'optoutMessage',
    rules: {
      validate: {
        ...(subscriberOptout
          ? {
              isRequired: createRequiredValidator('Please enter message'),
              withValidMinLength: createMinLengthValidator(20),
              isValidInput,
              withValidMaxLength: createMaxLengthValidator(1024),
            }
          : {}),
      },
    },
  });
  const {
    field: { value: embeddedPhone, onChange: setEmbeddedPhone },
  } = useController({
    control,
    name: 'embeddedPhone',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: subscriberHelp, onChange: setSubscriberHelp },
  } = useController({
    control,
    name: 'subscriberHelp',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });
  const {
    field: { value: helpKeywords, onChange: setHelpKeywords },
  } = useController({
    control,
    name: 'helpKeywords',
  });
  const {
    field: {
      value: helpMessage,
      onChange: setHelpMessage,
      onBlur: validateHelpMessage,
    },
    fieldState: { error: helpMessageError },
  } = useController({
    control,
    name: 'helpMessage',
    disabled: !subscriberHelp,
    rules: {
      validate: {
        ...(subscriberHelp
          ? {
              isRequired: createRequiredValidator('Please enter message'),
              withValidMinLength: createMinLengthValidator(20),
              isValidInput,
              withValidMaxLength: createMaxLengthValidator(1024),
            }
          : {}),
      },
    },
  });
  const {
    field: {
      disabled: ageGatedDisabled,
      value: ageGated,
      onChange: setAgeGated,
    },
  } = useController({
    control,
    name: 'ageGated',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });

  const {
    field: { value: directLending, onChange: setDirectLending },
  } = useController({
    control,
    name: 'directLending',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });

  const [additionalSetupType, setAdditionalSetupType] = useState(
    AdditionalSetupType.None
  );

  const handleDoneBtnClick = (setupType: AdditionalSetupType) => {
    let hasError = false;
    let updatedValues: Partial<RcsCampaign> = {};
    if (setupType === AdditionalSetupType.SubscriberOptin) {
      hasError = !!optinMessageError?.message;
      updatedValues = {
        optinMessage,
        optinKeywords: optinKeywords.join(', '),
      };
    } else if (setupType === AdditionalSetupType.EmbeddedLink) {
      hasError = !!embeddedLinkSampleError?.message;
      updatedValues = {
        embeddedLinkSample,
      };
    } else if (setupType === AdditionalSetupType.SubscriberOptout) {
      hasError = !!optoutMessageError?.message;
      updatedValues = {
        optoutMessage,
        optoutKeywords: optoutKeywords.join(', '),
      };
    } else if (setupType === AdditionalSetupType.SubscriberHelp) {
      hasError = !!helpMessageError?.message;
      updatedValues = {
        helpMessage,
        helpKeywords: helpKeywords.join(', '),
      };
    }

    if (!hasError) {
      setAdditionalSetupType(AdditionalSetupType.None);
      onUpdate?.(updatedValues);
    }
  };

  const handleAdditionSetupVisibilityChange = (
    type: AdditionalSetupType,
    open: boolean
  ) => {
    if (open) {
      setAdditionalSetupType(type);
    } else if (additionalSetupType === type) {
      // Prevent additional setup from closing when switching additional setup type
      setAdditionalSetupType(AdditionalSetupType.None);
    }
  };

  useEffect(() => {
    clearErrors();
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <Box
      data-testid="CampaignAttributesReadonlyPanel"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: 'l',
        rowGap: 's',
      }}
    >
      <CampaignAttributeNew
        type="view"
        id="subscriberOptIn"
        campaignInfo={campaign}
        value={subscriberOptin}
        label="Subscriber Opt-in"
        tooltip="Indicates whether the campaign is collecting and processing consumer opt-ins"
        additionalSetupActive={
          additionalSetupType === AdditionalSetupType.SubscriberOptin
        }
        additionalSetup={
          <MessageFlowAttributePopup
            defaultReadonly
            showCharCounter
            editable={!readonly}
            hideCloseButton
            label="Opt-in"
            keywordsTooltip="A keyword consumers can text to opt-in."
            messageTooltip="If consumers can text in a keyword, the response should include the Brand DBA name, confirmation of opt-in enrollment to a recurring message campaign, how to get help, and clear description of how to opt-out."
            keywords={optinKeywords}
            message={optinMessage}
            error={optinMessageError?.message}
            onKeywordsChange={setOptinKeywords}
            onMessageChange={setOptinMessage}
            onMessageBlur={validateOptinMessage}
            onClose={() =>
              handleDoneBtnClick(AdditionalSetupType.SubscriberOptin)
            }
          />
        }
        handleChange={(val) => {
          setSubscriberOptin(val);
          onUpdate?.({ subscriberOptin: val });
        }}
        setAdditionalSetupActive={(open) =>
          handleAdditionSetupVisibilityChange(
            AdditionalSetupType.SubscriberOptin,
            open
          )
        }
      />
      <CampaignAttributeNew
        type="view"
        id="embeddedLink"
        campaignInfo={campaign}
        label="Embedded Link"
        tooltip="Are you using an embedded link of any kind? Note that public URL shorteners (bitly, tinyurl) are not accepted."
        additionalSetupActive={
          additionalSetupType === AdditionalSetupType.EmbeddedLink
        }
        value={embeddedLink}
        additionalSetup={
          <EmbeddedLinkPopup
            defaultReadonly
            value={embeddedLinkSample}
            error={embeddedLinkSampleError?.message}
            onChange={setEmbeddedLinkSample}
            onBlur={validateEmbeddedLinkSample}
            onClose={() => handleDoneBtnClick(AdditionalSetupType.EmbeddedLink)}
            editable={!readonly}
          />
        }
        handleChange={(val) => {
          setEmbeddedLink(val);
          onUpdate?.({ embeddedLink: val });
        }}
        setAdditionalSetupActive={(open) =>
          handleAdditionSetupVisibilityChange(
            AdditionalSetupType.EmbeddedLink,
            open
          )
        }
      />
      <CampaignAttributeNew
        type="view"
        id="subscriberOptout"
        label="Subscriber Opt-Out"
        campaignInfo={campaign}
        tooltip="You are collecting and processing consumer opt-outs."
        additionalSetupActive={
          additionalSetupType === AdditionalSetupType.SubscriberOptout
        }
        value={subscriberOptout}
        additionalSetup={
          <MessageFlowAttributePopup
            defaultReadonly
            messageRequired
            showCharCounter
            editable={!readonly}
            hideCloseButton
            label="Opt-out"
            keywordsTooltip="A keyword consumers can text to opt-out."
            messageTooltip="The response to the STOP keyword may include the Brand DBA name but should include an acknowledgement of opt-out request and confirmation that no further messages will be sent."
            error={optoutMessageError?.message}
            keywords={optoutKeywords}
            message={optoutMessage}
            onKeywordsChange={setOptoutKeywords}
            onMessageChange={setOptoutMessage}
            onMessageBlur={validateOptoutMessage}
            onClose={() =>
              handleDoneBtnClick(AdditionalSetupType.SubscriberOptout)
            }
          />
        }
        handleChange={(val) => {
          setSubscriberOptout(val);
          onUpdate?.({ subscriberOptout: val });
        }}
        setAdditionalSetupActive={(open) =>
          handleAdditionSetupVisibilityChange(
            AdditionalSetupType.SubscriberOptout,
            open
          )
        }
      />
      <CampaignAttributeNew
        type={readonly ? 'view' : ''}
        id="embeddedPhone"
        campaignInfo={campaign}
        label="Embedded Phone Number"
        tooltip="Are you using an embedded phone number (except the required HELP information contact phone number)?"
        value={embeddedPhone}
        handleChange={(val) => {
          setEmbeddedPhone(val);
          onUpdate?.({ embeddedPhone: val });
        }}
      />
      <CampaignAttributeNew
        type="view"
        id="subscriberHelp"
        label="Subscriber Help"
        tooltip='You have implemented message reply providing customers on how they can contact the message sender after they reply with the "HELP" keyword.'
        additionalSetupActive={
          additionalSetupType === AdditionalSetupType.SubscriberHelp
        }
        value={subscriberHelp}
        additionalSetup={
          <MessageFlowAttributePopup
            defaultReadonly
            messageRequired
            showCharCounter
            editable={!readonly}
            hideCloseButton
            label="Help"
            keywordsTooltip="A keyword consumers use to get help or more information."
            messageTooltip="The response to HELP keyword may include the Brand DBA name and additional support contact information."
            error={helpMessageError?.message}
            keywords={helpKeywords}
            message={helpMessage}
            onKeywordsChange={setHelpKeywords}
            onMessageChange={setHelpMessage}
            onMessageBlur={validateHelpMessage}
            onClose={() =>
              handleDoneBtnClick(AdditionalSetupType.SubscriberHelp)
            }
          />
        }
        handleChange={(val) => {
          setSubscriberHelp(val);
          onUpdate?.({ subscriberHelp: val });
        }}
        setAdditionalSetupActive={(open) =>
          handleAdditionSetupVisibilityChange(
            AdditionalSetupType.SubscriberHelp,
            open
          )
        }
      />
      <CampaignAttributeNew
        type={readonly ? 'view' : ''}
        id="ageGated"
        campaignInfo={campaign}
        label="Age-Gated Content"
        tooltip="Will this campaign include any age-gated content as defined by Carrier and CTIA guidelines?"
        value={ageGated}
        handleChange={(val) => {
          setAgeGated(val);
          onUpdate?.({ ageGated: val });
        }}
        {...(editable && {
          disabledOption: ageGatedDisabled ? 'all' : undefined,
        })}
      />
      <CampaignAttributeNew
        type={readonly ? 'view' : ''}
        id="directLending"
        campaignInfo={campaign}
        label="Direct Lending or Loan Arrangement"
        tooltip="Will this campaign include content related to direct lending or other loan arrangements?"
        value={directLending}
        handleChange={(val) => {
          setDirectLending(val);
          onUpdate?.({ directLending: val });
        }}
      />
    </Box>
  );
};

export default ComplianceAttributesForm;
